import { DB_FIELD_MAX_LENGTH } from "@selector/shared/settings";
import { Component, type ReactElement } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, reduxForm, SubmissionError, type Validator } from "redux-form";

import { getCssColor } from "@/app-utils/color.ts";
import { type ISelectOption, simpleDateValidation, simpleFieldValidation } from "@/app-utils/form.ts";
import { type InputModifyProjectInput, type Project } from "@/graphql.ts";
import { getProjectLabel } from "@/redux/modules/config.ts";
import { modifyProject, validateProjectNameIsDefined } from "@/redux/modules/project-list.ts";
import { type IAppState } from "@/redux/reducer.ts";

import { Dialog } from "../core/overlays/Dialogs.js";
import CheckboxField, { CheckboxReduxFormField } from "../core/redux-form/CheckboxField.js";
import ColorPickerField, { ColorPickerReduxFormField } from "../core/redux-form/ColorPickerField.js";
import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import { ReactDateSelectReduxFormField } from "../core/redux-form/ReactDateSelectorField.js";
import ReactSelectField, { ReactSelectReduxFormField } from "../core/redux-form/ReactSelectField.js";

interface IEditProjectDialogOwnProps {
  onApply?: () => void;
  onCancel?: () => void;
  project: Project;
}

interface IEditProjectDialogAppStateProps {
  initialValues: Partial<FormData>;
}

interface IEditProjectDialogDispatchProps {
  modifyProject: (payload: InputModifyProjectInput) => Promise<Project>;
}

export interface IEditProjectDialogProps
  extends IEditProjectDialogAppStateProps,
    IEditProjectDialogDispatchProps,
    IEditProjectDialogOwnProps,
    InjectedFormProps<FormData, any> {}

function mapStateToProps(_state: IAppState, ownProps: IEditProjectDialogOwnProps): IEditProjectDialogAppStateProps {
  return {
    initialValues: {
      [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_NAME]: ownProps.project.name,
      [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_DESCRIPTION]: ownProps.project.description,
      [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_COLOR]: getCssColor(ownProps.project.color),
      [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_NOTIFY_MEMBERS]: false,
      [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_STATUS_NOTES]: ownProps.project.statusNotes,
      [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_END_NOTES]: ownProps.project.endNotes,
      [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_STATUS]: {
        value: ownProps.project.status,
        label: ownProps.project.status,
      },
      [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_ACTUAL_END_DATE]: ownProps.project.actualEndDate,
      [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_START_DATE]: ownProps.project.startDate,
      [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_INITIAL_END_DATE]: ownProps.project.initialEndDate,
      [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_PROJECTED_END_DATE]: ownProps.project.projectedEndDate,
    },
  };
}

function mapDispatchToProps(dispatch): IEditProjectDialogDispatchProps {
  return {
    modifyProject: (payload: InputModifyProjectInput) => dispatch(modifyProject(payload)),
  };
}

class EditProjectDialogImpl extends Component<IEditProjectDialogProps> {
  public static PROJECT_DIALOG_FIELD_NAME = "projectName";
  public static PROJECT_DIALOG_FIELD_DESCRIPTION = "projectDescription";
  public static PROJECT_DIALOG_FIELD_COLOR = "projectColor";
  public static PROJECT_DIALOG_FIELD_NOTIFY_MEMBERS = "notifyMembers";
  public static PROJECT_DIALOG_FIELD_STATUS_NOTES = "projectStatusNote";
  public static PROJECT_DIALOG_FIELD_END_NOTES = "projectEndNote";
  public static PROJECT_DIALOG_FIELD_STATUS = "projectStatus";
  public static PROJECT_DIALOG_FIELD_ACTUAL_END_DATE = "projectActualEndDate";
  public static PROJECT_DIALOG_FIELD_START_DATE = "projectStartDate";
  public static PROJECT_DIALOG_FIELD_INITIAL_END_DATE = "projectInitialEndDate";
  public static PROJECT_DIALOG_FIELD_PROJECTED_END_DATE = "projectProjectedEndDate";

  constructor(props: IEditProjectDialogProps) {
    super(props);
  }

  handleSubmitData = (values): Promise<Project> => {
    const payload: InputModifyProjectInput = {
      id: this.props.project.id,
      name: values[EditProjectDialogImpl.PROJECT_DIALOG_FIELD_NAME],
      color: values[EditProjectDialogImpl.PROJECT_DIALOG_FIELD_COLOR],
      description: values[EditProjectDialogImpl.PROJECT_DIALOG_FIELD_DESCRIPTION] || null,
      notifyMembers: values[EditProjectDialogImpl.PROJECT_DIALOG_FIELD_NOTIFY_MEMBERS] || false,
      statusNote: values[EditProjectDialogImpl.PROJECT_DIALOG_FIELD_STATUS_NOTES] || null,
      endNote: values[EditProjectDialogImpl.PROJECT_DIALOG_FIELD_END_NOTES] || null,
      status: values[EditProjectDialogImpl.PROJECT_DIALOG_FIELD_STATUS]?.value || null,
      actualEndDate: values[EditProjectDialogImpl.PROJECT_DIALOG_FIELD_ACTUAL_END_DATE] || null,
      startDate: values[EditProjectDialogImpl.PROJECT_DIALOG_FIELD_START_DATE] || null,
      initialEndDate: values[EditProjectDialogImpl.PROJECT_DIALOG_FIELD_INITIAL_END_DATE] || null,
      projectedEndDate: values[EditProjectDialogImpl.PROJECT_DIALOG_FIELD_PROJECTED_END_DATE] || null,
    };

    return this.props.modifyProject(payload).then(
      (fulfilled: Project) => {
        this.props.onApply();
        this.props.reset();
        return fulfilled;
      },
      // the following will throw an error that redux-form understands as a submission error, with a general error that will be
      // available from this.props.error
      (rejected: Error) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  render(): ReactElement {
    const formatOptions: ISelectOption[] = [
      { value: "Delayed", label: "Delayed" },
      { value: "Finished on time", label: "Finished on time" },
      { value: "Finished with delay", label: "Finished with delay" },
      { value: "New project", label: "New project" },
      { value: "On time", label: "On time" },
    ];

    return (
      <Dialog
        closeFn={() => {
          this.props.reset();
          this.props.onCancel?.();
        }}
        headerProps={{ title: `Modify ${this.props.project?.name ?? getProjectLabel(true, false)}` }}
        mainActionButtonProps={{
          label: "Save",
          isLoading: this.props.submitting,
          onPress: this.props.handleSubmit(this.handleSubmitData),
        }}
        footerMessage={this.props.error ? { message: this.props.error } : undefined}
      >
        <InputReduxFormField
          componentClass="input"
          name={EditProjectDialogImpl.PROJECT_DIALOG_FIELD_NAME}
          label={`${getProjectLabel(true, false)} name`}
          placeholder={`Enter ${getProjectLabel(false, false)} name`}
          maxLength={DB_FIELD_MAX_LENGTH.PROJECT.NAME}
          component={InputField}
          validate={simpleFieldValidation}
        />
        <ColorPickerReduxFormField
          name={EditProjectDialogImpl.PROJECT_DIALOG_FIELD_COLOR}
          title="Color"
          component={ColorPickerField}
          // tooltip not migrated yet since not used
        />
        <InputReduxFormField
          componentClass="textarea"
          name={EditProjectDialogImpl.PROJECT_DIALOG_FIELD_DESCRIPTION}
          label="Description"
          placeholder="Optional description"
          maxLength={DB_FIELD_MAX_LENGTH.PROJECT.DESCRIPTION}
          component={InputField}
        />
        <CheckboxReduxFormField
          name={EditProjectDialogImpl.PROJECT_DIALOG_FIELD_NOTIFY_MEMBERS}
          label={`Notify ${getProjectLabel(false, false)} members`}
          component={CheckboxField}
        />
        <ReactDateSelectReduxFormField
          horizontalLayout={false}
          key={`project-edit-start-date`}
          name={EditProjectDialogImpl.PROJECT_DIALOG_FIELD_START_DATE}
          title={"Start date"}
          withTime={false}
          placeholder="Select the start date..."
          validate={this.dateValidation}
        />
        <ReactDateSelectReduxFormField
          horizontalLayout={false}
          key={`project-edit-projected-end-date`}
          name={EditProjectDialogImpl.PROJECT_DIALOG_FIELD_PROJECTED_END_DATE}
          title={"Projected end date"}
          withTime={false}
          placeholder="Select the projected end date..."
          validate={this.dateValidation}
        />
        <ReactDateSelectReduxFormField
          horizontalLayout={false}
          key={`project-edit-initial-end-date`}
          name={EditProjectDialogImpl.PROJECT_DIALOG_FIELD_INITIAL_END_DATE}
          title={"Initial end date"}
          withTime={false}
          placeholder="Select the initial end date..."
          validate={this.dateValidation}
        />
        <ReactDateSelectReduxFormField
          horizontalLayout={false}
          key={`project-edit-actual-end-date`}
          name={EditProjectDialogImpl.PROJECT_DIALOG_FIELD_ACTUAL_END_DATE}
          title={"Actual end date"}
          withTime={false}
          placeholder="Select the actual end date..."
          validate={this.dateValidation}
        />
        <ReactSelectReduxFormField
          name={EditProjectDialogImpl.PROJECT_DIALOG_FIELD_STATUS}
          label="Status"
          placeholder="Please select..."
          options={formatOptions}
          component={ReactSelectField}
          isClearable={true}
          isSearchable={false}
        />
        <InputReduxFormField
          componentClass="textarea"
          name={EditProjectDialogImpl.PROJECT_DIALOG_FIELD_STATUS_NOTES}
          label="Status notes"
          placeholder="Optional status notes"
          maxLength={DB_FIELD_MAX_LENGTH.PROJECT.STATUS_NOTES}
          component={InputField}
        />
        <InputReduxFormField
          componentClass="textarea"
          name={EditProjectDialogImpl.PROJECT_DIALOG_FIELD_END_NOTES}
          label="End notes"
          placeholder="Optional end notes"
          maxLength={DB_FIELD_MAX_LENGTH.PROJECT.END_NOTES}
          component={InputField}
        />
      </Dialog>
    );
  }

  dateValidation: Validator = (value: any, _allValues?: any, _props?: any) => {
    if (!value) {
      return undefined;
    }

    return simpleDateValidation(value);
  };

  static async asyncValidate(values, dispatch, props: IEditProjectDialogProps) {
    if (!values) {
      return undefined;
    }

    if (values.projectName == undefined || (props.project && props.project.id) == undefined) {
      throw {
        [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_NAME]: "Project is undefined",
      };
    }

    if (await dispatch(validateProjectNameIsDefined(values.projectName, props.project.id))) {
      throw {
        [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_NAME]: `${getProjectLabel(true, false)} name is already in use`,
      };
    }
  }
}

const EditProjectDialog = connect<
  IEditProjectDialogAppStateProps,
  IEditProjectDialogDispatchProps,
  IEditProjectDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: "editProject",
    // validate: EditProjectDialogImpl.validate, // we use field level validation
    asyncValidate: EditProjectDialogImpl.asyncValidate,
    asyncBlurFields: [EditProjectDialogImpl.PROJECT_DIALOG_FIELD_NAME], // so that only blurring this field triggers the async validation
    initialValues: {},
  })(EditProjectDialogImpl) as any,
);

export { EditProjectDialog };
